import { fmtDateTime } from "../lib/format";

/**
 * Progress for a change-set apply, built to the four things a progress indicator has to answer
 * (Nielsen Norman Group, "Progress Indicators Make a Slow System Less Insufferable"):
 *
 *   1. is it working?      → a state line in plain words, and a bar that always moves
 *   2. how far along?      → "28 of 83 done", with a percentage — anything over ~10s needs a real
 *                            percent-done indicator, not a spinner
 *   3. how much longer?    → elapsed + a rough estimate from the measured rate, never a precise promise
 *   4. what is it doing?   → the object ("items in ExactOnline"), not internal statuses
 *
 * The reason this exists as its own component: the old block reported raw database statuses. During a
 * re-run it said "0 pending · 64 failed" while those 64 were being retried successfully — technically
 * true, and it read as "nothing is happening and everything is broken".
 */

export interface ApplyStatusView {
  running: boolean;
  paused: boolean;
  budgetPaused: boolean;
  interrupted: boolean;
  target: string;
  total: number;
  applied: number;
  failed: number;
  pending: number;
  dryRunCount: number;
  skipped: number;
  retrying: number;
  percent: number;
  lanes: number | null;
  startedAt: string | Date | null;
}

const nl = (n: number) => n.toLocaleString("nl-NL");

/** "about 4 minutes" — deliberately vague; a precise countdown that slips is worse than none. */
function roughly(seconds: number): string {
  if (seconds < 45) return "less than a minute";
  const min = Math.round(seconds / 60);
  if (min < 60) return `about ${min} minute${min === 1 ? "" : "s"}`;
  const h = Math.floor(min / 60);
  return `about ${h} hour${h === 1 ? "" : "s"} ${min % 60} min`;
}

function elapsedSeconds(startedAt: string | Date | null): number | null {
  if (!startedAt) return null;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}

export function ApplyProgress({ as, system }: { as: ApplyStatusView; system: string }) {
  const done = as.applied + as.dryRunCount + as.skipped;
  const retrying = as.retrying > 0;
  // During a retry every operation is already "processed", so the stored percentage sits at 100% while
  // work continues. Measure by what has actually succeeded.
  const pct = retrying && as.total > 0 ? Math.round((done / as.total) * 100) : as.percent;
  const remaining = retrying ? as.retrying : as.pending;

  const elapsed = elapsedSeconds(as.startedAt);
  // Estimate from the rate this run has actually achieved — no estimate until there is something to
  // measure, rather than a made-up number in the first seconds.
  const eta = elapsed && elapsed > 20 && done > 0 && remaining > 0 ? roughly((elapsed / done) * remaining) : null;

  const state = as.running
    ? retrying
      ? `Retrying ${nl(as.retrying)} item${as.retrying === 1 ? "" : "s"} that failed earlier`
      : `Sending ${nl(as.total)} change${as.total === 1 ? "" : "s"} to ${system}`
    : as.budgetPaused
      ? "Paused — the daily API budget is used up"
      : as.paused
        ? "Paused — press Resume to finish"
        : as.interrupted
          ? "Stopped before finishing — press Resume to continue"
          : remaining > 0 || as.failed > 0
            ? "Finished, but not everything went through"
            : "Finished";

  const tone = as.running ? "indigo" : as.interrupted || as.paused || as.budgetPaused || as.failed > 0 ? "amber" : "emerald";
  const barColor = tone === "indigo" ? "bg-indigo-600" : tone === "amber" ? "bg-amber-500" : "bg-emerald-500";
  const textColor = tone === "indigo" ? "text-indigo-800" : tone === "amber" ? "text-amber-800" : "text-emerald-800";

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className={`text-sm font-semibold ${textColor}`}>
          {as.running && <span className="mr-1.5 inline-block h-2 w-2 animate-pulse rounded-full bg-current align-middle" />}
          {state}
        </span>
        <span className="tabular-nums text-xs text-slate-500">{pct}%</span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full transition-all duration-500 ${barColor}`} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>

      {/* The one number that matters, in words — not database statuses. */}
      <div className="text-xs text-slate-700">
        <span className="font-medium tabular-nums">{nl(done)} of {nl(as.total)} done</span>
        {remaining > 0 && <span className="text-slate-500"> · {nl(remaining)} to go</span>}
        {!as.running && as.failed > 0 && (
          <span className="ml-1 font-medium text-red-600">· {nl(as.failed)} need attention</span>
        )}
      </div>

      {/* Time: what has passed, and a deliberately rough estimate of what is left. */}
      {(elapsed != null || as.lanes) && (
        <div className="text-[11px] tabular-nums text-slate-400">
          {as.startedAt && <>started {fmtDateTime(as.startedAt)}</>}
          {elapsed != null && <> · running for {roughly(elapsed)}</>}
          {eta && <> · {eta} left</>}
          {as.lanes && as.lanes > 1 && <> · {as.lanes} at a time</>}
        </div>
      )}

      {as.running && (
        <p className="text-[11px] text-slate-400">
          You can leave this page — it keeps running on the server and the numbers pick up where they were.
        </p>
      )}
    </div>
  );
}
